//© 2025 Kaitlyn Troxell
import { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  addDoc, 
  deleteDoc, 
  doc,
  where,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';

function useProjects(user) {
  const [projects, setProjects] = useState([]);
  
  // Get stored project ID from localStorage
  const storedProjectId = localStorage.getItem('currentProjectId');
  const [currentProjectId, setCurrentProjectIdState] = useState(storedProjectId);
  const [loading, setLoading] = useState(true);

  // Wrapper function to update state AND localStorage
  const setCurrentProjectId = (projectId) => {
    setCurrentProjectIdState(projectId);
    if (projectId) {
      localStorage.setItem('currentProjectId', projectId);
    } else {
      localStorage.removeItem('currentProjectId');
    }
  };

  // Fetch all projects
  useEffect(() => {
    const fetchProjects = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const projectsQuery = query(
          collection(db, 'projects'),
          orderBy('created_at', 'desc')
        );

        const querySnapshot = await getDocs(projectsQuery);
        const projectsList = [];

        querySnapshot.forEach((docSnap) => {
          projectsList.push({
            id: docSnap.id,
            ...docSnap.data()
          });
        });

        setProjects(projectsList);
        
        // If we have a stored project ID, verify it still exists
        if (storedProjectId) {
          const projectExists = projectsList.find(p => p.id === storedProjectId);
          if (!projectExists && projectsList.length > 0) {
            // If stored project no longer exists, select first project
            setCurrentProjectId(projectsList[0].id);
          } else if (!projectExists) {
            // Clear invalid stored ID
            localStorage.removeItem('currentProjectId');
            setCurrentProjectIdState(null);
          }
        } else if (!currentProjectId && projectsList.length > 0) {
          // If no project selected and we have projects, select the first one
          setCurrentProjectId(projectsList[0].id);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [user]);

  // Create new project
  const createProject = async (projectName) => {
    try {
      // 1. Create the project with all sections enabled by default
      const docRef = await addDoc(collection(db, 'projects'), {
        name: projectName,
        created_at: serverTimestamp(),
        user_id: user.uid,
        enabledSections: [
          'exterior-siding', 'exterior-roof', 'exterior-garage',
          'exterior-brick', 'exterior-concrete', 'exterior-deck',
          'exterior-landscape', 'interior-fireplace', 'interior-windows',
          'interior-basement', 'interior-stairs', 'interior-insulation',
          'interior-mechanicals', 'interior-appliances', 'interior-flooring',
          'interior-tile', 'interior-bathroom', 'interior-kitchen',
          'interior-cabinets', 'interior-walls'
        ]
      });

      const newProjectId = docRef.id;

      // 2. Fetch the newly created project to get the actual timestamp
      const projectDoc = await getDoc(docRef);
      const newProject = {
        id: newProjectId,
        ...projectDoc.data()
      };

      // 3. Fetch task templates (clean tasks not tied to any project)
      const templatesQuery = query(
        collection(db, 'task_templates'),
        orderBy('order', 'asc')
      );
      const templatesSnapshot = await getDocs(templatesQuery);

      // 4. Copy templates to new project tasks (all fresh, no contractor/completed/notes)
      const taskPromises = [];
      templatesSnapshot.forEach(templateDoc => {
        const template = templateDoc.data();
        
        // Clean the data - ensure no undefined values
        const taskData = {
          title: template.title || '',
          phase: template.phase || null,
          section_id: template.section_id || null, // Add this field
          order: template.order || 0,
          phase_order: template.phase_order || 0,
          contractor: null,
          completed: false,
          date_scheduled: null,
          date_completed: null,
          notes: null,
          project_id: newProjectId,
          created_at: serverTimestamp()
        };
        
        taskPromises.push(
          addDoc(collection(db, 'tasks'), taskData)
        );
      });
      await Promise.all(taskPromises);

      // 5. Initialize empty selections document for the new project
      const { setDoc } = await import('firebase/firestore');
      const selectionsRef = doc(db, 'projects', newProjectId, 'selections', 'formData');
      await setDoc(selectionsRef, {
        created_at: serverTimestamp(),
        lastModified: serverTimestamp(),
        modifiedBy: user.uid,
        status: 'draft'
      });

      // 6. Update local state with new project
      setProjects(prev => [newProject, ...prev]);
      
      // Automatically select the newly created project
      setCurrentProjectId(newProjectId);

      return newProjectId;
    } catch (err) {
      console.error("Error creating project with tasks:", err);
      throw err;
    }
  };

  // Delete project and all associated data
  const deleteProject = async (projectId) => {
    try {
      // Delete all selections subcollection documents
      const selectionsQuery = query(
        collection(db, 'projects', projectId, 'selections')
      );
      const selectionsSnapshot = await getDocs(selectionsQuery);
      const selectionsDeletePromises = [];
      selectionsSnapshot.forEach((selectionDoc) => {
        selectionsDeletePromises.push(
          deleteDoc(doc(db, 'projects', projectId, 'selections', selectionDoc.id))
        );
      });
      await Promise.all(selectionsDeletePromises);

      // Delete the project
      await deleteDoc(doc(db, 'projects', projectId));

      // Delete all tasks associated with this project
      const tasksQuery = query(
        collection(db, 'tasks'),
        where('project_id', '==', projectId)
      );
      const tasksSnapshot = await getDocs(tasksQuery);
      const taskDeletePromises = [];
      tasksSnapshot.forEach((taskDoc) => {
        taskDeletePromises.push(deleteDoc(doc(db, 'tasks', taskDoc.id)));
      });
      await Promise.all(taskDeletePromises);

      // Delete all punch list items associated with this project
      const punchListQuery = query(
        collection(db, 'punch_items'),
        where('project_id', '==', projectId)
      );
      const punchListSnapshot = await getDocs(punchListQuery);
      const punchListDeletePromises = [];
      punchListSnapshot.forEach((punchListDoc) => {
        punchListDeletePromises.push(deleteDoc(doc(db, 'punch_items', punchListDoc.id)));
      });
      await Promise.all(punchListDeletePromises);

      // Delete all calendar events associated with this project
      const eventsQuery = query(
        collection(db, 'calendar_events'),
        where('project_id', '==', projectId)
      );
      const eventsSnapshot = await getDocs(eventsQuery);
      const eventDeletePromises = [];
      eventsSnapshot.forEach((eventDoc) => {
        eventDeletePromises.push(deleteDoc(doc(db, 'calendar_events', eventDoc.id)));
      });
      await Promise.all(eventDeletePromises);

      // Update local state
      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      // If deleted project was current, switch to first available
      if (currentProjectId === projectId) {
        const remainingProjects = projects.filter(p => p.id !== projectId);
        if (remainingProjects.length > 0) {
          setCurrentProjectId(remainingProjects[0].id);
        } else {
          setCurrentProjectId(null);
        }
      }
    } catch (err) {
      console.error("Error deleting project:", err);
      throw err;
    }
  };

  // Run once to migrate existing projects
  const migrateProjects = async () => {
    const { collection, getDocs, doc, updateDoc } = await import('firebase/firestore');
    
    const projectsSnapshot = await getDocs(collection(db, 'projects'));
    
    for (const projectDoc of projectsSnapshot.docs) {
      if (!projectDoc.data().enabledSections) {
        // Enable all sections for existing projects
        await updateDoc(doc(db, 'projects', projectDoc.id), {
          enabledSections: [
            'exterior-siding', 'exterior-roof', 'exterior-garage',
            'exterior-brick', 'exterior-concrete', 'exterior-deck',
            'exterior-landscape', 'interior-fireplace', 'interior-windows',
            'interior-basement', 'interior-stairs', 'interior-insulation',
            'interior-mechanicals', 'interior-appliances', 'interior-flooring',
            'interior-tile', 'interior-bathroom', 'interior-kitchen',
            'interior-cabinets', 'interior-walls'
          ]
        });
      }
    }
  };

  const currentProject = projects.find(p => p.id === currentProjectId);

  return {
    projects,
    currentProject,
    currentProjectId,
    setCurrentProjectId,
    createProject,
    deleteProject,
    loading
  };
}

export default useProjects;